import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import FullCalendar, { CalendarOptions } from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import jaLocale from '@fullcalendar/core/locales/ja';
import { CalendarHarvestRecord, CalendarPlantingPlan, CalendarShippingRecord, CalendarWorkRecord, CalendarEvent, CalendarFilter } from '@/types';
import { DatePointApi, EventClickArg, EventHoveringArg } from '@fullcalendar/common/main';

interface CalendarAttribute {
  key: string;
  highlight: boolean;
  dates: Date | Date[] | null;
}

@Component({
  components: {
    FullCalendar,
  }
})
export default class VueCalendar extends Vue {
  @Prop({})
  filter!: CalendarFilter;
  private events: CalendarEvent[] = [];
  private showDatePicker = false;
  private attributes: CalendarAttribute[] = [
    {
      key: 'selected',
      highlight: true,
      dates: null,
    },
  ];
  private options: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    eventOrder: "eventOrder",
    events: this.getEvents,
    locale: jaLocale,
    headerToolbar: {
      left: 'prev,title,next',
      center: 'today datePicker',
      right: 'dayGridMonth,dayGridWeek'
    },
    dayMaxEvents: true,
    dateClick: this.dateClick,
    eventClick: this.eventClick,
    eventMouseEnter: this.onEventMouseEnter,
    initialDate: (this.$route.query.date) as any,
    height: 'calc(100vh - 36px)',
    // 既存のヘッダーボタンをオーバーライドし、クリック時に選択日付のハイライト表示化処理を挿入
    customButtons: {
      prev: {
        click: this.onPrevClick,
      },
      next: {
        click: this.onNextClick,
      },
      today: {
        text: "今日",
        click: this.onTodayClick,
      },
      datePicker: {
        text: "日付",
        click: this.onDatePickerClick,
      },
      filter: {
        text: "",
        click: this.onFilteringClick,
      },
      dayGridMonth: {
        text: "月",
        click: this.onDayGridMonthClick,
      },
      dayGridWeek: {
        text: "週",
        click: this.onDayGridWeekClick,
      },
    }
  };
  private dispDate = ''
  @Prop({})
  deleteId!: string;
  @Prop({})
  applyFilter!: boolean;
  private calMonth!: { month: number; year: number; };

  private handleResize() {
    const width = window.innerWidth
    this.options = {
      ...this.options,
      dayMaxEvents: width <= 1000,
      headerToolbar: {
        left: 'prev,title,next',
        center: width > 1000 ? 'today datePicker' : 'today datePicker',
        right: width > 1000 ? 'dayGridMonth,dayGridWeek' : 'dayGridMonth,dayGridWeek,filter'
      },
      titleFormat: width > 1000 ? undefined : {year: 'numeric', month: 'numeric'},
      views: {
        dayGridMonth: {
          dayHeaderFormat: {weekday: 'short'}
        },
        dayGridWeek: {
          dayHeaderFormat: {weekday: 'short', day: 'numeric'}
        }
      }
    }
  }

  private mounted() {
    this.dispDate = this.$route.query.date ? this.$route.query.date as string : localStorage.getItem('calendarDate') ? localStorage.getItem('calendarDate') as string : this.formatDate(new Date());
    this.selectDate(this.dispDate);
    this.$emit('closeDetail');
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
    this.setSelectedDay(this.dispDate)
  }

  private get refs(): any {
    return this.$refs;
  }

  private get isAdmin(): boolean {
    let isAdmin = false;
    const loginUser = this.$store.getters.loginUser;
    loginUser.role.forEach((roleName: string) => {
      if (roleName === 'ADMIN') {
        isAdmin = true;
      }
    });
    return isAdmin;
  }

  private onDatePickerClick() { 
    this.showDatePicker = true
    this.$emit('datePickerToggle', true);
    this.$root.$emit('header-inert', true)
  }

  private closeModal() {
    this.showDatePicker = false;
    this.$emit('datePickerToggle', false)
    this.$root.$emit('header-inert', false)
  }

  private moveToDate(date: any) {
    this.closeModal();
    const calendarApi = this.refs.calendar.getApi();
    calendarApi.gotoDate(date.id); 
    this.selectDate(date.id);
  }

  private dateClick(info: DatePointApi) {
    const dateStr = info.dateStr;
    this.selectDate(dateStr);
  }

  private selectDate(date: string) {
    const selDate = new Date(date);
    this.attributes[0].dates = selDate;
    this.calMonth = { month: selDate.getMonth() + 1, year: selDate.getFullYear()};
    localStorage.setItem('calendarDate', date);
    this.setSelectedDay(date)
    this.dispDate = date
    this.$emit('dateClick', date);
  }

  private setSelectedDay(date: string) {
    const selectDateElements = document.getElementsByClassName('fc-select-day');
    [].forEach.call(selectDateElements, function(e: HTMLElement) {
      e.classList.remove('fc-select-day')
    });
    document.querySelectorAll("td[data-date='" + date + "']").forEach((e: Element) => {
      e.classList.add('fc-select-day');
    });
  }

  private eventClick(info: EventClickArg) {
    const plantingId = info.event._def.extendedProps.plantingId;
    const workItemId = info.event._def.extendedProps.workItemId;
    // eslint-disable-next-line no-extra-boolean-cast
    if ( !!plantingId && !!workItemId) {
      const selectDateElements = document.getElementsByClassName('fc-select-day');
      [].forEach.call(selectDateElements, function(e: HTMLElement) {
        e.classList.remove('fc-select-day')
      });
      const plantingPlanIds = {
        plantingId: plantingId,
        workItemId: workItemId
      };
      this.$emit('eventClick', plantingPlanIds);
    // eslint-disable-next-line no-extra-boolean-cast
    } else if ( !!info.event.start){
      this.selectDate(this.formatDate(info.event.start));
    }
  }

  private onEventMouseEnter(info: EventHoveringArg) {
    info.el.title = info.event.title
  }

  private onNextClick() {
    const calendarApi = this.refs.calendar.getApi();
    calendarApi.next()
    this.setSelectedDay(this.dispDate)
  }

  private onPrevClick() {
    const calendarApi = this.refs.calendar.getApi();
    calendarApi.prev()
    this.setSelectedDay(this.dispDate)
  }

  private onTodayClick() {
    const calendarApi = this.refs.calendar.getApi();
    calendarApi.today()
    this.selectDate(this.formatDate(new Date()))
  }

  private onDayGridMonthClick() {
    const calendarApi = this.refs.calendar.getApi();
    calendarApi.changeView('dayGridMonth')
    this.setSelectedDay(this.dispDate)
  }

  private onDayGridWeekClick() {
    const calendarApi = this.refs.calendar.getApi();
    calendarApi.changeView('dayGridWeek')
    this.setSelectedDay(this.dispDate)
  }

  private onFilteringClick() {
    this.$emit('openModal');
  }

  private async getEvents(info: any, successCallback: any): Promise<any> {
    this.events.splice(0);
    const startDate: string = this.formatDate(info.start);
    const end = new Date(info.end)
    end.setDate(end.getDate() - 1)
    const endDate: string = this.formatDate(end);

    const getWorkRecord = this.getWorkRecords(startDate, endDate);
    const getHarvestRecord = this.getHarvestRecords(startDate, endDate);
    let getShippingRecord = undefined;
    if (this.isAdmin) {
      getShippingRecord = this.getShippingRecords(startDate, endDate);
    }
    const getPlantingPlans = this.getPlantingPlans(startDate, endDate);

    await Promise.all([getWorkRecord, getHarvestRecord, getShippingRecord, getPlantingPlans]);
    this.events.sort((a, b) => {
      let prevOrder = 0
      let nextOrder = 0
      if (a.className !== b.className) {
        if (a.className?.includes('jisseki')) {
          prevOrder = 1
        }
        if (a.className?.includes('yotei')) {
          prevOrder = 2
        }
        if (a.className?.includes('syukaku')) {
          prevOrder = 3
        }
        if (a.className?.includes('syukka')) {
          prevOrder = 4
        }
        if (b.className?.includes('jisseki')) {
          nextOrder = 1
        }
        if (b.className?.includes('yotei')) {
          nextOrder = 2
        }
        if (b.className?.includes('syukaku')) {
          nextOrder = 3
        }
        if (b.className?.includes('syukka')) {
          nextOrder = 4
        }
        return prevOrder - nextOrder
      }

      if (a.title > b.title) {
        return 1
      }
      if (a.title < b.title) {
        return -1
      }

      return 0
    })
    this.events = this.events.map((calenderEvent, index) => {
      return {
        ...calenderEvent,
        eventOrder: ++index
      }
    })
    await successCallback(
      this.events
    )
  }

  private async getWorkRecords(startDate: string, endDate: string): Promise<any> {
    const api = '/work_records/calendar';
    let params;
    const kinds = [];
    if (this.filter.dispWorkActual) { kinds.push('ACTUAL'); }
    if (this.filter.dispWorkSchedule) { kinds.push('SCHEDULE'); }
    if (!this.applyFilter) {
      params = {
        start_date: startDate,
        end_date: endDate
      }
    } else {
      params = {
        start_date: startDate,
        end_date: endDate,
        kinds: kinds,
        planting_ids: this.filter.plantingIds,
        agri_field_ids: this.filter.agriFieldIds,
        worker_ids: this.filter.workerIds,
        machine_ids: this.filter.machineIds,
        work_item_ids: this.filter.workItemIds,
        include_no_planting: this.filter.includeNoPlanting,
        include_no_worker: this.filter.includeNoWorker,
        include_no_machine: this.filter.includeNoMachine
      }
    }
    return await this.$apiClient.doRequest('post', api, params).then((ret) => {
      const workRecords: CalendarEvent[] = ret.filter((workRecord: CalendarWorkRecord, index: number, array: CalendarWorkRecord[]) =>
        array.findIndex(w => workRecord.work_item.name === w.work_item.name && workRecord.work_date === w.work_date && workRecord.kind === w.kind) === index
      ).map((workRecord: CalendarWorkRecord) => {
        return {
          id: workRecord.id,
          title: workRecord.work_item.name,
          start: workRecord.work_date,
          end: workRecord.work_date,
          display: 'list-item',
          className: workRecord.kind === 'ACTUAL' ? 'plan_name is_jisseki' : 'plan_name is_yotei'
        };
      });
      this.events = this.events.concat(workRecords);
    });
  }

  private async getHarvestRecords(startDate: string, endDate: string): Promise<any> {
    if (!this.filter.dispHarvest) {
      return;
    }
    const api = '/harvest_records/calendar';
    let params;
    if (!this.applyFilter) {
      params = {
        start_date: startDate,
        end_date: endDate
      }
    } else {
      params = {
        start_date: startDate,
        end_date: endDate,
        planting_ids: this.filter.plantingIds,
        agri_field_ids: this.filter.agriFieldIds
      }
    }
    return await this.$apiClient.doRequest('post', api, params).then((ret) => {
      const harvestRecords: CalendarEvent[] = ret.filter((harvestRecord: CalendarHarvestRecord, index: number, array: CalendarHarvestRecord[]) =>
      array.findIndex(h => harvestRecord.harvest_date === h.harvest_date) === index
    ).map(function(harvestRecord: CalendarHarvestRecord) {
        return {
          id: harvestRecord.id,
          title: '収穫記録',
          start: harvestRecord.harvest_date,
          end: harvestRecord.harvest_date,
          display: 'list-item',
          className: 'plan_name is_syukaku'
        };
      });
      this.events = this.events.concat(harvestRecords);
    });
  }

  private async getShippingRecords(startDate: string, endDate: string): Promise<any> {
    if (!this.filter.dispShipping) {
      return;
    }
    const api = '/admin/shipping_records/calendar';
    let params;
    if (!this.applyFilter) {
      params = {
        start_date: startDate,
        end_date: endDate
      }
    } else {
      params = {
        start_date: startDate,
        end_date: endDate,
        planting_ids: this.filter.plantingIds,
        agri_field_ids: this.filter.agriFieldIds
      }
    }
    return await this.$apiClient.doRequest('post', api, params).then((ret) => {
      const shippingRecords: CalendarEvent[] = ret.filter((shippingRecord: CalendarShippingRecord, index: number, array: CalendarShippingRecord[]) =>
        array.findIndex(s => shippingRecord.shipping_date === s.shipping_date) === index
      ).map(function(shippingRecord: CalendarShippingRecord) {
        return {
          id: shippingRecord.id,
          title: '出荷記録',
          start: shippingRecord.shipping_date,
          end: shippingRecord.shipping_date,
          display: 'list-item',
          className: 'plan_name is_syukka'
        };
      });
      this.events = this.events.concat(shippingRecords);
    });
  }

  private async getPlantingPlans(startDate: string, endDate: string): Promise<any> {
    if (!this.filter.dispPlantingPlan) {
      return;
    }
    const api = 'plantings/plans/calendar';
    let params;
    if (!this.applyFilter) {
      params = {
        start_date: startDate,
        end_date: endDate
      }
    } else {
      params = {
        start_date: startDate,
        end_date: endDate,
        planting_ids: this.filter.plantingIds,
        work_item_ids: this.filter.workItemIds,
      }
    }
    const that = this;
    return await this.$apiClient.doRequest('post', api, params).then((ret) => {
      const plantingPlans: CalendarEvent[] = ret.map(function(plantingPlan: CalendarPlantingPlan) {
        const date = new Date(plantingPlan.working_end_date);
        date.setDate(date.getDate() + 1);
        return {
          title: `${plantingPlan.planting.name} ${plantingPlan.work_item.name}`,
          start: plantingPlan.working_start_date,
          end: that.formatDate(date),
          plantingId: plantingPlan.planting.id,
          workItemId: plantingPlan.work_item.id,
          color: plantingPlan.planting.color
        }
      })
      this.events = this.events.concat(plantingPlans);
    });
  }

  private updateFilter() {
    if (this.applyFilter) {
      const api = this.refs.calendar.getApi();
      api.refetchEvents();
    }
  }

  @Watch('deleteId')
  private refreshEvent() {
    const api = this.refs.calendar.getApi();
    const event = api.getEventById(this.deleteId);
    api.refetchEvents();
  }

  private formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = ('00' + (date.getMonth() + 1)).slice(-2);
    const d = ('00' + date.getDate()).slice(-2);

    return (y + '-' + m + '-' + d);
  }

  private get computedInitialDate(): string {
    try {
      return this.$route.query.date ? this.$route.query.date as string : localStorage.getItem('calendarDate') ? localStorage.getItem('calendarDate') as string 
          : this.formatDate(new Date());
    } catch (error) {
      return this.formatDate(new Date());
    }
  } 
}
